import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { navigate } from '@/hooks/useRoute';
import { useStore } from '@/context/StoreContext';
import { restGet, signOut } from '@/lib/supabase';
import './AdminLayoutV3.css';
import './AdminControlCenterV5.css';

const items=[
  ['⌂','דשבורד','/admin'],
  ['◇','מוצרים','/admin/products'],
  ['₪','הזמנות','/admin/orders'],
  ['◫','אנליטיקה','/admin/analytics'],
  ['⌕','חיפושים','/admin/searches'],
  ['CJ','CJ925 Import','/admin/cj925-import'],
  ['▦','קטגוריות','/admin/categories'],
  ['✦','דף הבית','/admin/content/home'],
  ['⌁','עמודי תוכן','/admin/pages'],
  ['▧','מדיה','/admin/media'],
  ['⚙','הגדרות','/admin/settings'],
] as const;

const pageMeta=(path:string)=>{
  if(path==='/admin') return ['דשבורד','סקירה עסקית בזמן אמת'];
  if(path.startsWith('/admin/products')) return ['ניהול מוצרים','קטלוג / מוצרים'];
  if(path.startsWith('/admin/orders')) return ['הזמנות ותשלומים','מכירות / HYP · MAX'];
  if(path.startsWith('/admin/analytics')) return ['אנליטיקה','תנועה / משפך / מקורות'];
  if(path.startsWith('/admin/searches')) return ['חיפושים באתר','Search Intelligence'];
  if(path.startsWith('/admin/cj925-import')) return ['CJ925 Import','ייבוא ספק / CJ925'];
  if(path.startsWith('/admin/categories')) return ['קטגוריות','קטלוג / קטגוריות'];
  if(path.startsWith('/admin/content/home')) return ['דף הבית','תוכן / דף הבית'];
  if(path.startsWith('/admin/pages')) return ['עמודי תוכן','תוכן / עמודים'];
  if(path.startsWith('/admin/media')) return ['מדיה','ספריית מדיה'];
  if(path.startsWith('/admin/settings')) return ['הגדרות','מערכת / הגדרות'];
  return ['MIELLEY Admin','מרכז הבקרה'];
};

type SmallOrder={status:string;created_at:string;admin_seen_at:string|null;fulfillment_status?:string};

export function AdminLayout({children}:{children:ReactNode}){
 const {dataMode}=useStore();
 const path=window.location.pathname;
 const [globalQ,setGlobalQ]=useState('');
 const [menuOpen,setMenuOpen]=useState(false);
 const [orderPulse,setOrderPulse]=useState({newOrders:0,pending:0,active:0});
 const [title,crumb]=pageMeta(path);

 const loadPulse=useCallback(async()=>{
   if(dataMode!=='supabase')return;
   try{
     const rows=await restGet<SmallOrder[]>('mielley_orders','select=status,created_at,admin_seen_at,fulfillment_status&order=created_at.desc&limit=150');
     setOrderPulse({
       newOrders:rows.filter(row=>row.status==='paid'&&!row.admin_seen_at).length,
       pending:rows.filter(row=>row.status==='payment_pending').length,
       active:rows.filter(row=>row.status==='paid'&&String(row.fulfillment_status||'received')!=='delivered').length,
     });
   }catch{}
 },[dataMode]);

 useEffect(()=>{void loadPulse();const timer=window.setInterval(()=>void loadPulse(),30000);return()=>window.clearInterval(timer)},[loadPulse]);

 const logout=async()=>{await signOut();window.location.reload()};
 const search=(e:FormEvent)=>{
   e.preventDefault();
   const value=globalQ.trim();
   if(value) sessionStorage.setItem('mielley_admin_global_search',value);
   navigate('/admin/products');
 };
 const go=(to:string)=>{setMenuOpen(false);navigate(to)};

 return <div className={`mielley-admin-v2-shell mav3-shell ${menuOpen?'menu-open':''}`} dir="rtl">
   {menuOpen&&<button className="mav3-mobile-overlay" aria-label="סגירת תפריט" onClick={()=>setMenuOpen(false)}/>} 
   <aside className="mielley-admin-v2-sidebar">
    <div className="mav2-brand" onClick={()=>go('/admin')}>
      <div className="mav2-crown" aria-hidden="true">♢</div>
      <strong>MIELLEY</strong>
      <small>FINE JEWELRY</small>
    </div>

    <nav className="mav2-nav" aria-label="ניווט אדמין">
      {items.map(([icon,label,to])=><button
        key={to}
        className={path===to||(to!='/admin'&&path.startsWith(to+'/'))?'active':''}
        onClick={()=>go(to)}
      ><i>{icon}</i><span>{label}</span>{to==='/admin/orders'&&orderPulse.newOrders>0&&<em className="mav3-nav-badge">{orderPulse.newOrders}</em>}</button>)}
    </nav>

    <div className="mav2-sidebar-divider"/>
    <div className="mav2-quick-title">פעולות מהירות</div>
    <div className="mav2-quick-actions">
      <button onClick={()=>go('/admin/products/new')}><span>＋</span> הוספת מוצר חדש</button>
      <button onClick={()=>go('/admin/orders')}><span>₪</span> הזמנות ותשלומים{(orderPulse.newOrders>0||orderPulse.pending>0)&&<b className="mav3-pending-dot">{orderPulse.newOrders||orderPulse.pending}</b>}</button>
      <button onClick={()=>go('/admin/cj925-import')}><span>CJ</span> ייבוא CJ925</button>
      <button onClick={()=>go('/')}><span>◉</span> צפייה בחנות</button>
    </div>

    <div className="mav2-sidebar-bottom">
      <div className="mav2-admin-id">
        <span className="mav2-avatar">M</span>
        <div><b>MIELLEY Admin</b><small>{dataMode==='supabase'?'Cloud Admin':'Local Preview'}</small></div>
      </div>
      {dataMode==='supabase'&&<button className="mav2-logout" onClick={()=>void logout()}>יציאה מהאדמין</button>}
    </div>
   </aside>

   <main className="mielley-admin-v2-main">
    <header className="mav2-topbar">
      <div className="mav2-page-meta"><button className="mav2-menu" aria-label="תפריט" onClick={()=>setMenuOpen(value=>!value)}>☰</button><div><h1>{title}</h1><small>{crumb}</small></div></div>
      <form className="mav2-global-search" onSubmit={search}>
        <span>⌕</span>
        <input value={globalQ} onChange={e=>setGlobalQ(e.target.value)} placeholder="חיפוש מוצרים, MIELLEY SKU, מק״ט ספק..." />
      </form>
      <div className="mav2-top-user">
        <button title="עזרה">?</button><button className="mav3-orders-shortcut" title="הזמנות חדשות" onClick={()=>go('/admin/orders')}>✉{orderPulse.newOrders>0&&<em>{orderPulse.newOrders}</em>}</button><span className="mav2-avatar small">M</span>
        <div><b>MIELLEY</b><small>{dataMode==='supabase'?'מחובר לענן':'תצוגה מקומית'}</small></div>
      </div>
    </header>
    <section className="mav2-page">{children}</section>
   </main>
 </div>;
}
